import { useEffect, useState } from "react";
import { Percent, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULTS = { gst_rate: 18, hsn_sac: "998311", invoice_prefix: "INV", place_of_supply: "Chhattisgarh", gstin: "", pan: "" };

export default function TaxSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/tax").then((r) => setForm({ ...DEFAULTS, ...(r.data || {}) })).catch(() => setForm(DEFAULTS)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/tax", { ...form, gst_rate: parseFloat(form.gst_rate) || 0 });
      toast.success("Tax settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading tax settings…</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="tax-settings-page">
      <PageHeader overline="Invoices" title="GST & Tax Settings" subtitle="Firm-level billing configuration applied to new invoices" />

      <Card className="p-6 border-border space-y-4">
        <SectionTitle icon={Percent}>Tax</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Default GST Rate (%)</Label>
            <Select value={String(form.gst_rate)} onValueChange={set("gst_rate")}>
              <SelectTrigger data-testid="tax-rate"><SelectValue /></SelectTrigger>
              <SelectContent>{["0", "5", "12", "18", "28"].map((r) => <SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>HSN / SAC Code</Label><Input value={form.hsn_sac} onChange={set("hsn_sac")} placeholder="998311" data-testid="tax-hsn" /></div>
          <div className="space-y-1.5"><Label>Firm GSTIN</Label><Input value={form.gstin} onChange={set("gstin")} placeholder="22ABCDE1234F1Z5" data-testid="tax-gstin" /></div>
          <div className="space-y-1.5"><Label>Firm PAN</Label><Input value={form.pan} onChange={set("pan")} placeholder="ABCDE1234F" data-testid="tax-pan" /></div>
          <div className="space-y-1.5"><Label>Place of Supply</Label><Input value={form.place_of_supply} onChange={set("place_of_supply")} data-testid="tax-pos" /></div>
          <div className="space-y-1.5"><Label>Invoice Number Prefix</Label><Input value={form.invoice_prefix} onChange={set("invoice_prefix")} data-testid="tax-prefix" /></div>
        </div>
        <div className="rounded-md bg-muted border border-border p-3">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Numbering preview</div>
          <div className="font-mono text-sm text-primary mt-1" data-testid="numbering-preview">
            {form.invoice_prefix || "INV"}-{new Date().toISOString().slice(2, 4)}{String(new Date().getMonth() + 1).padStart(2, "0")}-0001
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-tax-btn">
          <FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : "Save Tax Settings"}
        </Button>
      </Card>
    </div>
  );
}
